import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Phone, PhoneOff, Mic, MicOff, Radio, AlertTriangle, Copy, Check } from 'lucide-react';
import { CallOrb } from './CallOrb.jsx';
import { useVoiceChatWS } from './useVoiceChatWS.js';
import { fetchTestDialerSetup } from '../../../../api/dialerTest.js';
import { overlayDismiss } from '../../../../lib/overlayDismiss';

const STATE_LABEL_KEYS = {
    idle: 'preview.state.idle',
    connecting: 'preview.state.connecting',
    listening: 'preview.state.listening',
    user_speaking: 'preview.state.userSpeaking',
    thinking: 'preview.state.thinking',
    ai_speaking: 'preview.state.aiSpeaking',
    ended: 'preview.state.ended',
    error: 'preview.state.error',
};

const STATE_DOT = {
    idle: 'bg-ink-300',
    connecting: 'bg-accent-500 animate-pulse',
    listening: 'bg-accent-500',
    user_speaking: 'bg-accent-700',
    thinking: 'bg-accent-500 animate-pulse',
    ai_speaking: 'bg-accent-700',
    ended: 'bg-ink-300',
    error: 'bg-red-500',
};

function formatElapsed(startMs) {
    if (!startMs) return '0:00';
    const sec = Math.floor((Date.now() - startMs) / 1000);
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * ErrorPanel — 依錯誤類型顯示對應引導
 *
 * insecure_context → 完整 Chrome flag 步驟（HTTP origin 無法用 mic）
 * permission_denied → 提示用戶允許 mic 權限（無 Chrome flag 步驟）
 * no_device / device_busy / 其他 → 簡單訊息
 */
function ErrorPanel({ error, onRetry }) {
    const { t } = useTranslation('wsTest');
    const [copied, setCopied] = useState(false);
    const code = error?.code || 'unknown';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';

    const handleCopy = async (text) => {
        let ok = false;
        // navigator.clipboard 只在 secure context (HTTPS/localhost) 可用
        // 這個面板本身就是因為 HTTP insecure context 才出現 → clipboard API 必然失效
        if (navigator.clipboard && window.isSecureContext) {
            try { await navigator.clipboard.writeText(text); ok = true; } catch {}
        }
        // HTTP fallback：execCommand('copy') 在 insecure context 仍可用
        if (!ok) {
            try {
                const ta = document.createElement('textarea');
                ta.value = text;
                ta.style.position = 'fixed';
                ta.style.top = '0';
                ta.style.left = '0';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                ok = document.execCommand('copy');
                document.body.removeChild(ta);
            } catch {}
        }
        if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        }
    };

    if (code === 'insecure_context') {
        return (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3.5 text-xs leading-relaxed">
                <div className="flex items-start gap-2 mb-2">
                    <AlertTriangle size={14} strokeWidth={2} className="text-amber-700 shrink-0 mt-0.5" />
                    <div>
                        <div className="font-semibold text-ink-900">{t('preview.error.insecure.title')}</div>
                        <div className="text-ink-600 mt-0.5">
                            {t('preview.error.insecure.desc')}
                        </div>
                    </div>
                </div>

                <ol className="space-y-1.5 text-ink-700 ml-1 list-decimal list-inside marker:text-ink-400 marker:font-mono">
                    <li>
                        {t('preview.error.insecure.step1')}
                        <div className="mt-1 ml-5">
                            <button
                                onClick={() => handleCopy('chrome://flags/#unsafely-treat-insecure-origin-as-secure')}
                                className="flex items-start gap-1 max-w-full px-1.5 py-0.5 bg-white border border-cream-300 rounded font-mono text-[10.5px] text-ink-700 hover:border-accent-300 hover:bg-accent-50 transition text-left"
                                title={t('preview.error.insecure.copyTitle')}
                            >
                                <span className="break-all min-w-0">chrome://flags/#unsafely-treat-insecure-origin-as-secure</span>
                                {copied ? <Check size={10} className="text-green-600 shrink-0 mt-0.5" /> : <Copy size={10} className="shrink-0 mt-0.5" />}
                            </button>
                        </div>
                    </li>
                    <li>{t('preview.error.insecure.step2pre')}<span className="font-medium text-ink-900">Enabled</span></li>
                    <li>
                        {t('preview.error.insecure.step3')}
                        <div className="mt-1 ml-5">
                            <button
                                onClick={() => handleCopy(origin)}
                                className="flex items-start gap-1 max-w-full px-2 py-1 bg-white border border-cream-300 rounded font-mono text-[11px] text-ink-700 hover:border-accent-300 hover:bg-accent-50 transition text-left"
                                title={t('preview.error.insecure.copyTitle')}
                            >
                                <span className="break-all min-w-0">{origin || 'http://43.213.126.202:8082'}</span>
                                {copied ? <Check size={11} className="text-green-600 shrink-0 mt-0.5" /> : <Copy size={11} className="shrink-0 mt-0.5" />}
                            </button>
                        </div>
                    </li>
                    <li>{t('preview.error.insecure.step4pre')}<span className="font-medium text-ink-900">Relaunch</span>{t('preview.error.insecure.step4post')}</li>
                    <li>{t('preview.error.insecure.step5')}</li>
                </ol>

                <div className="mt-3 text-[10.5px] text-ink-500 italic">
                    {t('preview.error.insecure.footer')}
                </div>
            </div>
        );
    }

    if (code === 'permission_denied') {
        return (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3.5 text-xs leading-relaxed">
                <div className="flex items-start gap-2">
                    <Mic size={14} strokeWidth={2} className="text-amber-700 shrink-0 mt-0.5" />
                    <div>
                        <div className="font-semibold text-ink-900">{t('preview.error.permission.title')}</div>
                        <div className="text-ink-600 mt-0.5">
                            {t('preview.error.permission.desc')}
                        </div>
                    </div>
                </div>
                {onRetry && (
                    <button
                        onClick={onRetry}
                        className="mt-2 text-xs text-accent-700 hover:text-accent-800 font-medium underline underline-offset-2"
                    >
                        {t('preview.error.retry')}
                    </button>
                )}
            </div>
        );
    }

    if (code === 'no_device') {
        return (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs">
                <div className="flex items-start gap-2">
                    <AlertTriangle size={14} className="text-red-600 shrink-0 mt-0.5" />
                    <div>
                        <div className="font-semibold text-ink-900">{t('preview.error.noDevice.title')}</div>
                        <div className="text-ink-600 mt-0.5">{t('preview.error.noDevice.desc')}</div>
                    </div>
                </div>
            </div>
        );
    }

    if (code === 'device_busy') {
        return (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs">
                <div className="flex items-start gap-2">
                    <AlertTriangle size={14} className="text-red-600 shrink-0 mt-0.5" />
                    <div>
                        <div className="font-semibold text-ink-900">{t('preview.error.deviceBusy.title')}</div>
                        <div className="text-ink-600 mt-0.5">{t('preview.error.deviceBusy.desc')}</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs">
            <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="text-red-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                    <div className="font-semibold text-ink-900">{t('preview.error.generic.title')}</div>
                    <div className="text-ink-600 mt-0.5 break-all">{error?.message || 'unknown'}</div>
                </div>
            </div>
            {onRetry && (
                <button
                    onClick={onRetry}
                    className="mt-2 text-xs text-accent-700 hover:text-accent-800 font-medium underline underline-offset-2"
                >
                    {t('preview.error.retry')}
                </button>
            )}
        </div>
    );
}

/**
 * PreviewCallModal — 模擬通話 dialog
 *
 * Visual hierarchy (top → bottom):
 *   1. Header (eyebrow + avatar name + close)
 *   2. Status pill + elapsed timer
 *   3. CallOrb (center)
 *   4. Subtitle area (user + AI, only most recent turn)
 *   5. Control bar (mic mute + hang up)
 *
 * 對話對象是 B 端商家 — 視覺保持 admin-console cream/ink/accent 暖色調，
 * 動畫 subtle 不卡通。
 */
export function PreviewCallModal({ open, onClose, avatarId, hostId, did, avatarName, primaryLanguage = 'zh-TW', testDialerSetup = null }) {
    const { t } = useTranslation('wsTest');
    const [elapsed, setElapsed] = useState('0:00');

    // testDialerSetup 存在 → 隨機抽一筆 contact + 對應 greeting 跑外播模擬；沒有 → 一般流程
    const [setup, setSetup] = useState(null);
    const [pickedIdx, setPickedIdx] = useState(0);
    const [setupLoaded, setSetupLoaded] = useState(false);

    const pickedContact = setup?.contacts?.[pickedIdx] || null;
    const contactVars = pickedContact ? (pickedContact.variables || {}) : null;
    const campaignId = pickedContact ? 'preview-test' : null;
    const greetingOverride = (() => {
        if (!pickedContact || !setup) return null;
        const cat = pickedContact.variables?.category;
        const tpl = (cat && setup.greetingTemplates?.[cat]) || setup.greetingTemplate;
        if (!tpl) return null;
        return tpl.replace(/\{\{(\w+)\}\}/g, (_, k) => pickedContact.variables?.[k] || '');
    })();

    // 模擬通話用 avatar 自己的 primaryLanguage（泰文 avatar 就跑泰文開場白），不要寫死 zh-TW
    // —— 否則 webcall 後端會把開場白翻成 zh-TW（Thai avatar 變中文開場白 bug）。
    const voice = useVoiceChatWS({ avatarId, hostId, did, languageCode: primaryLanguage || 'zh-TW', contactVars, campaignId, greetingOverride });

    // 載入 setup + 隨機抽一筆
    //   - 母帳號：owner-scoped fetch 拿最新（剛在撥號名單編輯完也 fresh）
    //   - 子帳號：fetch 會 403/空 → fallback 用上層從 scoped avatar doc 傳進來的 testDialerSetup
    useEffect(() => {
        if (!open || !avatarId) return;
        fetchTestDialerSetup(avatarId).catch((err) => {
            console.warn('[PreviewCallModal] fetchTestDialerSetup failed:', err?.status, err?.message, '→ falling back to prop');
            return null;
        }).then((s) => {
            const eff = s || testDialerSetup || null;
            setSetup(eff);
            if (eff?.contacts?.length) setPickedIdx(Math.floor(Math.random() * eff.contacts.length));
            setSetupLoaded(true);
        });
        // ⚠️ 不在 prop/avatarId 變動時 reset setup —— 否則 parent 重抓 avatar(每次 chat 完成都會)
        // 會把 setup 清空,期間若 user 點「重新撥打」(onClick → voice.start)會讀到 null contactVarsRef
        // → WS 空 connect → AI 講「親愛的長輩」沒姓。reset 只在 modal close 時做(下面那個 useEffect)。
    }, [open, avatarId, testDialerSetup]);

    // ⚠️ 不在 modal close 時 reset setup —— 否則重開 modal 時 state='ended' 仍亮著「重新撥打」按鈕,
    // 用戶點下去 voice.start() 觸發時 setup/refs 還沒被新 fetch 重新填,讀到 NULL contactVarsRef
    // → WS 空 connect → AI 講「親愛的長輩」沒姓。setup 跨 open/close 留著,component unmount React 自動清。

    // setup 載入完即可 start（沒 setup 也 start — 跑一般流程）
    useEffect(() => {
        if (!open || !setupLoaded) return;
        if (voice.state === 'idle') voice.start();
        // eslint-disable-next-line
    }, [open, setupLoaded, pickedIdx]);

    // 關閉時 reset(hangUp + state→idle + 清字幕)→ 確保下次重開是全新通話,不會看到上次的 ended UI
    useEffect(() => {
        if (!open && voice.state !== 'idle') {
            voice.reset();
        }
        // eslint-disable-next-line
    }, [open]);

    // Elapsed timer
    useEffect(() => {
        if (!voice.callStartTime || voice.state === 'ended' || voice.state === 'error') return;
        const t = setInterval(() => setElapsed(formatElapsed(voice.callStartTime)), 1000);
        return () => clearInterval(t);
    }, [voice.callStartTime, voice.state]);

    if (!open) return null;

    const isLive = ['listening', 'user_speaking', 'thinking', 'ai_speaking'].includes(voice.state);
    const canMute = isLive;

    return (
        <div
            className="fixed inset-0 bg-ink-900/40 backdrop-blur-md z-50 grid place-items-center p-4"
            {...overlayDismiss(() => voice.state === 'ended' || voice.state === 'error' ? onClose() : null)}
        >
            <div
                className="bg-cream-50 border border-cream-200 rounded-xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden max-h-[90vh]"
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundImage: 'radial-gradient(circle at top, rgba(232, 213, 178, 0.25) 0%, transparent 60%)',
                }}
            >
                {/* === Header === */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200/80">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-400 inline-flex items-center gap-1.5">
                            <Radio size={11} strokeWidth={2} /> preview call
                        </div>
                        <h2 className="heading-display text-lg font-semibold text-ink-900 mt-0.5 truncate max-w-[280px]">
                            {avatarName || t('preview.title')}
                        </h2>
                    </div>
                    <button
                        onClick={() => { voice.reset(); onClose(); }}
                        className="size-9 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition"
                        aria-label={t('common:actions.close')}
                    >
                        <X size={18} />
                    </button>
                </div>

                {voice.state === 'error' ? (
                    /* === Error view — 獨立緊湊佈局，不套用通話 UI（無 orb / 狀態列 / 麥克風鈕）=== */
                    <>
                        <div className="px-6 py-5 overflow-y-auto">
                            <ErrorPanel error={voice.error} onRetry={voice.start} />
                        </div>
                        <div className="px-6 py-4 bg-white/40 border-t border-cream-200/80 flex items-center justify-end gap-2.5">
                            <button
                                onClick={() => { voice.reset(); onClose(); }}
                                className="inline-flex items-center justify-center px-4 py-2.5 rounded-full text-sm text-ink-600 hover:text-ink-900 hover:bg-cream-100 transition font-medium"
                            >
                                {t('common:actions.close')}
                            </button>
                        </div>
                    </>
                ) : (
                    /* === Call view === */
                    <>
                        {/* Status pill */}
                        <div className="px-6 pt-5 pb-2 flex items-center justify-center">
                            <div className="inline-flex items-center gap-2.5 px-3 py-1.5 bg-white/70 border border-cream-200 rounded-full shadow-sm">
                                <span className={`size-1.5 rounded-full ${STATE_DOT[voice.state] || 'bg-ink-300'}`} />
                                <span className="text-xs font-medium text-ink-700">
                                    {STATE_LABEL_KEYS[voice.state] ? t(STATE_LABEL_KEYS[voice.state]) : voice.state}
                                </span>
                                {isLive && (
                                    <>
                                        <span className="text-ink-300 text-xs">·</span>
                                        <span className="font-mono text-[11px] text-ink-500 tnum">{elapsed}</span>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Orb */}
                        <div className="px-6 py-6 flex items-center justify-center min-h-[300px]">
                            <CallOrb state={voice.state} avatarName={avatarName} />
                        </div>

                        {/* Subtitles */}
                        <div className="px-6 pb-5 min-h-[90px] flex flex-col gap-2.5">
                            {voice.userSubtitle && (
                                <div className="flex items-start gap-2">
                                    <span className="font-mono text-[10px] uppercase tracking-wider text-ink-400 pt-0.5 shrink-0 w-7">{t('preview.you')}</span>
                                    <span className="text-sm text-ink-600 leading-relaxed">{voice.userSubtitle}</span>
                                </div>
                            )}
                            {voice.aiSubtitle && (
                                <div className="flex items-start gap-2">
                                    <span className="font-mono text-[10px] uppercase tracking-wider text-accent-700 pt-0.5 shrink-0 w-7">AI</span>
                                    <span className="text-sm text-ink-900 leading-relaxed">
                                        {voice.aiSubtitle}
                                        {voice.state === 'ai_speaking' && (
                                            <span className="inline-block w-1 h-3.5 bg-accent-700 ml-0.5 align-middle animate-pulse" />
                                        )}
                                    </span>
                                </div>
                            )}
                            {!voice.userSubtitle && !voice.aiSubtitle && voice.state === 'listening' && (
                                <div className="text-xs text-ink-400 italic text-center">{t('preview.prompt')}</div>
                            )}
                        </div>

                        {/* Control bar */}
                        <div className="px-6 py-4 bg-white/40 border-t border-cream-200/80 flex items-center justify-center gap-3">
                            <button
                                onClick={() => voice.setMuted(!voice.muted)}
                                disabled={!canMute}
                                className={[
                                    'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm transition',
                                    voice.muted
                                        ? 'bg-ink-800 text-cream-50'
                                        : 'bg-white text-ink-700 border border-cream-300 hover:border-accent-300',
                                    !canMute ? 'opacity-50 cursor-not-allowed' : '',
                                ].join(' ')}
                            >
                                {voice.muted ? <MicOff size={15} /> : <Mic size={15} />}
                                <span className="font-medium">{voice.muted ? t('preview.muted') : t('preview.mic')}</span>
                            </button>

                            <button
                                onClick={() => { voice.reset(); onClose(); }}
                                className={voice.state === 'ended'
                                    ? "inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm bg-ink-700 text-cream-50 hover:bg-ink-800 transition font-medium shadow-md"
                                    : "inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm bg-red-600 text-white hover:bg-red-700 transition font-medium shadow-md"
                                }
                            >
                                <PhoneOff size={15} />
                                <span>{voice.state === 'ended' ? t('common:actions.close') : t('preview.hangUp')}</span>
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

